/**
 * voice-v8 — Termine nach Tagen ordnen (Kalender-Karte). (20.10.2026, Martin: „den Kalender überarbeiten“)
 *
 * Reine Rechnung, kein DOM: aus der Liste von calendar_list werden Tage in Wiener Zeit. Ganztägige Termine
 * (Datum ohne Uhrzeit) stehen oben, ihr Ende ist EXKLUSIV (Ende „2026-10-19“ heißt: letzter Tag der 18.). Termine mit
 * Uhrzeit stehen am Tag ihres Beginns. Für HEUTE wird markiert, was vorbei ist, was läuft und was als Nächstes kommt.
 */
import { entitiesAufloesen } from "../../shared/html-text.js";
import { TAG_FORM, tagPlus, wienTag } from "../../shared/wien-zeit.js";

export interface Termin {
  id?: string;
  summary?: string;
  start?: string | null;
  end?: string | null;
  location?: string | null;
  description?: string;
  attendees?: string[];
  /** Teilnehmer ohne Anzeigenamen oder über die Grenze hinaus — nur die Zahl. */
  attendeesWeitere?: number;
  htmlLink?: string;
  hangoutLink?: string;
}

export type Stand = "vorbei" | "jetzt" | "naechster" | "";

export interface Eintrag {
  termin: Termin;
  /** Der Tag (Wiener Zeit), unter dem dieser Eintrag steht. */
  tag: string;
  ganztag: boolean;
  /** Mehrtägig: dies ist ein Folgetag (bei Ganztag wie bei Terminen mit Uhrzeit). */
  fortsetzung: boolean;
  /** Bei Ganztag über mehrere Tage: letzter Tag (inklusive). */
  letzterTag?: string;
  beginn?: Date;
  ende?: Date;
  stand: Stand;
}

export interface Tag {
  tag: string;
  /** Letzter Tag einer zusammengefassten Folge freier Tage. */
  bis?: string;
  ganztags: Eintrag[];
  termine: Eintrag[];
  frei: boolean;
}

export interface Ordnung {
  tage: Tag[];
  /** Termine ohne lesbaren Beginn — nie stillschweigend weg. */
  unklar: Termin[];
}

/** Ein Fenster darüber hinaus zeigt nur die Tage vom ersten bis zum letzten Termin. */
const FENSTER_TAGE_MAX = 31;
/** Ein einzelner Termin steht höchstens an so vielen Tagen (ohne Fenster: nie unbegrenzt). */
const MEHRTAGE_MAX = 62;

const ist = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

function istTag(v: unknown): v is string {
  return ist(v) && TAG_FORM.test(v.trim());
}

function alsDatum(v: unknown): Date | null {
  if (!ist(v)) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ordneEin(tage: Map<string, Tag>, tag: string): Tag {
  let t = tage.get(tag);
  if (!t) {
    t = { tag, ganztags: [], termine: [], frei: false };
    tage.set(tag, t);
  }
  return t;
}

/**
 * Termine nach Tagen. `fenster` ist der abgefragte Zeitraum (Wiener Tage, beide inklusive); ohne ihn oder bei
 * unvollständiger Liste (`vollstaendig === false`) reichen die Tage nur bis zum letzten gelieferten Termin.
 */
export function ordneNachTagen(items: readonly Termin[], jetzt: Date, fenster?: { from?: unknown; to?: unknown; vollstaendig?: unknown }): Ordnung {
  const tage = new Map<string, Tag>();
  const unklar: Termin[] = [];
  const grenzen = fensterGrenzen(fenster);
  /** Mehrtägige Termine werden auf das abgefragte Fenster begrenzt, nie darüber hinaus gezeigt. */
  const imFenster = (tag: string): boolean => !grenzen || (tag >= grenzen[0] && tag <= grenzen[1]);
  /** Die Zählung beginnt am Fenster, nicht am Terminbeginn: ein Termin von vor drei Monaten steht heute trotzdem da. */
  const ersterImFenster = (erster: string): string => (grenzen && erster < grenzen[0] ? grenzen[0] : erster);
  for (const termin of items) {
    if (istTag(termin.start)) {
      // Ganztägig. Ende exklusiv; fehlt es oder liegt es nicht nach dem Beginn, ist es ein Tag.
      const erster = termin.start.trim();
      const ende = istTag(termin.end) && termin.end.trim() > erster ? termin.end.trim() : tagPlus(erster, 1);
      const letzter = tagPlus(ende, -1);
      for (let tag = ersterImFenster(erster), n = 0; tag <= letzter && n < MEHRTAGE_MAX; tag = tagPlus(tag, 1), n++) {
        if (!imFenster(tag)) break;
        ordneEin(tage, tag).ganztags.push({ termin, tag, ganztag: true, fortsetzung: tag !== erster, letzterTag: letzter !== erster ? letzter : undefined, stand: "" });
      }
      continue;
    }
    const beginn = alsDatum(termin.start);
    if (!beginn) {
      unklar.push(termin);
      continue;
    }
    const ende0 = alsDatum(termin.end);
    const ende = ende0 && ende0 > beginn ? ende0 : undefined;
    // Mit Uhrzeit über Mitternacht: an jedem Tag zeigen, den der Termin berührt. Endet er genau um 00:00,
    // gehört dieser Moment schon zum Folgetag (23:00–00:00 bleibt ein Termin des einen Tages).
    const erster = wienTag(beginn);
    const letzter = ende ? wienTag(new Date(ende.getTime() - 1)) : erster;
    for (let tag = ersterImFenster(erster), n = 0; tag <= letzter && n < MEHRTAGE_MAX; tag = tagPlus(tag, 1), n++) {
      if (!imFenster(tag)) break;
      ordneEin(tage, tag).termine.push({ termin, tag, ganztag: false, fortsetzung: tag !== erster, beginn, ende, stand: "" });
    }
  }
  for (const t of tage.values()) t.termine.sort((a, b) => a.beginn!.getTime() - b.beginn!.getTime());

  markiereHeute(tage.get(wienTag(jetzt)), jetzt);
  return { tage: mitFreienTagen(tage, fensterTage(tage, fenster)), unklar };
}

/** Heute: vorbei (Ende erreicht; ohne Ende nach einer Stunde), läuft gerade, erster der kommenden. */
function markiereHeute(heute: Tag | undefined, jetzt: Date): void {
  if (!heute) return;
  let naechster = false;
  for (const e of heute.termine) {
    const ende = e.ende ?? new Date(e.beginn!.getTime() + 3600_000);
    if (ende <= jetzt) e.stand = "vorbei";
    else if (e.beginn! <= jetzt) e.stand = "jetzt";
    else if (!naechster) {
      e.stand = "naechster";
      naechster = true;
    }
  }
}

/** Das abgefragte Fenster, wenn die Karte es kennt (Wiener Tage, beide inklusive). */
function fensterGrenzen(fenster?: { from?: unknown; to?: unknown }): [string, string] | null {
  if (!fenster || !istTag(fenster.from) || !istTag(fenster.to)) return null;
  const von = fenster.from.trim();
  const bis = fenster.to.trim();
  return bis < von ? null : [von, bis];
}

function fensterTage(tage: Map<string, Tag>, fenster?: { from?: unknown; to?: unknown; vollstaendig?: unknown }): [string, string] | null {
  const belegt = [...tage.keys()].sort();
  const erster = belegt[0];
  const letzter = belegt[belegt.length - 1];
  const nurTermine: [string, string] | null = erster && letzter ? [erster, letzter] : null;
  const grenzen = fensterGrenzen(fenster);
  if (!grenzen) return nurTermine;
  const [von, bis0] = grenzen;
  let bis = bis0;
  // Bei gekürzter Liste ist nach dem letzten Termin nichts über „frei“ bekannt.
  if (fenster?.vollstaendig === false && letzter && letzter < bis) bis = letzter;
  // Sehr weite Fenster (Rückblick, Quartal): nur Tage mit Terminen und die freien dazwischen.
  if (tagPlus(von, FENSTER_TAGE_MAX) < bis) return nurTermine;
  // Das Fenster wird nie erweitert: alle Termine liegen schon darin.
  return [von, bis < von ? von : bis];
}

/** Freie Tage dazwischen: ein einzelner als eine Zeile, zwei oder mehr in Folge als eine Zeile mit Zeitraum. */
function mitFreienTagen(tage: Map<string, Tag>, bereich: [string, string] | null): Tag[] {
  if (!bereich) return [];
  const aus: Tag[] = [];
  let lauf: Tag | null = null;
  for (let tag = bereich[0], n = 0; tag <= bereich[1] && n < 400; tag = tagPlus(tag, 1), n++) {
    const t = tage.get(tag);
    if (t) {
      lauf = null;
      aus.push(t);
    } else if (lauf) {
      lauf.bis = tag;
    } else {
      lauf = { tag, ganztags: [], termine: [], frei: true };
      aus.push(lauf);
    }
  }
  return aus;
}

/** „45 Min“, „1 Std“, „1 Std 30 Min“ — leer bei 0 und ab 24 Stunden (dann steht das Ende da). */
export function dauerText(beginn: Date, ende: Date): string {
  const min = Math.round((ende.getTime() - beginn.getTime()) / 60_000);
  if (min <= 0 || min >= 24 * 60) return "";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h === 0 ? `${m} Min` : m === 0 ? `${h} Std` : `${h} Std ${m} Min`;
}

/** Beschreibung als reiner Text: Zeilenumbrüche aus HTML, Tags und Entities weg, Zeilen gekürzt auf das Nötige. */
export function beschreibungZeilen(roh: string): string[] {
  const ohneTags = roh
    .replace(/<\s*br\s*\/?>|<\/\s*(?:p|div|li|h[1-6])\s*>/gi, "\n")
    .replace(/<[^>]*>/g, "");
  return entitiesAufloesen(ohneTags)
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((z) => z.replace(/[\u200b\ufeff]/g, "").trim())
    .filter((z, i, a) => z !== "" || (a[i - 1] ?? "") !== "")
    .filter((z, i, a) => !(z === "" && (i === 0 || i === a.length - 1)));
}
